import {createAction, createReducer} from 'redux-act';

import {ApplicationStoreType} from 'reducers';
import {FULFILLED} from 'redux-promise-middleware';
import {AxiosResponse} from 'axios';
import {OneQuoteItem} from 'constants/flow/flowTypesCart';
import {getContactsPdp} from 'features/contacts/selectors/contacts/contacts';
import {fetchQuoteItemV2} from 'actions/cart/cart';

export type FieldStateType = {disabled: boolean; error: string};
export type FieldValueType = string | number;
export type PrescriptionSideValuesType = {
    sph: FieldValueType;
    bc: FieldValueType;
    dia: FieldValueType;
    add: FieldValueType;
    cyl: FieldValueType;
    axi: FieldValueType;
    color: FieldValueType;
    boxes: FieldValueType;
};
export type PresriptionSideStatesType = {
    sph: FieldStateType;
    bc: FieldStateType;
    dia: FieldStateType;
    add: FieldStateType;
    cyl: FieldStateType;
    axi: FieldStateType;
    color: FieldStateType;
    boxes: FieldStateType;
};

export type PrescriptionValuesType = {
    od: PrescriptionSideValuesType;
    os: PrescriptionSideValuesType;
    state_od: PresriptionSideStatesType;
    state_os: PresriptionSideStatesType;
};

export type FieldsType = {
    od?: Partial<PrescriptionSideValuesType>;
    os?: Partial<PrescriptionSideValuesType>;
    state_od?: Partial<PresriptionSideStatesType>;
    state_os?: Partial<PresriptionSideStatesType>;
};

export const initialState = {
    od: {
        sph: '',
        bc: '',
        dia: '',
        add: '',
        cyl: '',
        axi: '',
        color: '',
        boxes: 0,
    },
    os: {
        sph: '',
        bc: '',
        dia: '',
        add: '',
        cyl: '',
        axi: '',
        color: '',
        boxes: 0,
    },
    state_od: {
        sph: {disabled: false, error: ''},
        bc: {disabled: false, error: ''},
        dia: {disabled: false, error: ''},
        add: {disabled: false, error: ''},
        cyl: {disabled: false, error: ''},
        axi: {disabled: false, error: ''},
        color: {disabled: false, error: ''},
        boxes: {disabled: false, error: ''},
    },
    state_os: {
        sph: {disabled: false, error: ''},
        bc: {disabled: false, error: ''},
        dia: {disabled: false, error: ''},
        add: {disabled: false, error: ''},
        cyl: {disabled: false, error: ''},
        axi: {disabled: false, error: ''},
        color: {disabled: false, error: ''},
        boxes: {disabled: false, error: ''},
    },
};

/**
 * List of actions
 */
export const setFields = createAction<{
    od?: Partial<PrescriptionSideValuesType>;
    os?: Partial<PrescriptionSideValuesType>;
    state_od?: Partial<PresriptionSideStatesType>;
    state_os?: Partial<PresriptionSideStatesType>;
}>('CONTACTS_WIZARD_SET_FIELDS');
export const resetFieldsState = createAction('CONTACTS_WIZARD_RESET_STATES');
export const fullResetFields = createAction('CONTACTS_WIZARD_FULL_RESET_FIELDS');

/**
 * Prescription values reducer
 */
export const prescriptionValues = createReducer<PrescriptionValuesType>({}, initialState)
    /**
     * Subscribe actions
     * https://www.npmjs.com/package/redux-act#typescript
     */
    .on(
        setFields,
        (
            state: PrescriptionValuesType,
            payload: {
                os?: Partial<PrescriptionSideValuesType>;
                od?: Partial<PrescriptionSideValuesType>;
                state_os?: Partial<PresriptionSideStatesType>;
                state_od?: Partial<PresriptionSideStatesType>;
            },
        ): PrescriptionValuesType => ({
            os: {...state.os, ...payload.os},
            od: {...state.od, ...payload.od},
            state_od: {...state.state_od, ...payload.state_od},
            state_os: {...state.state_os, ...payload.state_os},
        }),
    )
    .on(
        `${fetchQuoteItemV2}_${FULFILLED}`,
        (
            state: PrescriptionValuesType,
            payload: AxiosResponse<OneQuoteItem>,
        ): PrescriptionValuesType => {
            if (!('prescription' in payload.data) || !('od' in payload.data.prescription)) {
                return state;
            }
            return {
                ...state,
                od: payload.data.prescription.od,
                os: payload.data.prescription.os,
            };
        },
    )
    .on(
        resetFieldsState,
        (state: PrescriptionValuesType): PrescriptionValuesType => ({
            ...state,
            state_od: initialState.state_od,
            state_os: initialState.state_os,
        }),
    )
    .on(fullResetFields, (): PrescriptionValuesType => initialState);

/**
 * Get CL prescription values
 *
 * @param {ApplicationStoreType} store - redux store
 * @returns {PrescriptionValuesType} CL prescription values
 */
export const getPrescriptionValues = (store: ApplicationStoreType) =>
    getContactsPdp(store).prescriptionValues;
